import { memo, useCallback, useMemo, useState } from 'react';
import Panel from '@/features/report/shared/Panel/Panel';
import { formatTagLabel } from '@/shared/utils/tagLabel';
import { TagChip, TagChipRow } from './TagChip';
import { TagCombobox } from './TagCombobox';
import { normalizeTagInput } from './utils/normalizeTagInput';
import styles from './TagsPanel.module.css';

interface TagsPanelProps {
  catalog: string[];
  value: string[];
  onChange: (tags: string[]) => void;
  disabled?: boolean;
}

function TagsPanel({
  catalog,
  value,
  onChange,
  disabled = false,
}: TagsPanelProps) {
  const [draft, setDraft] = useState('');

  const selectedSet = useMemo(() => new Set(value), [value]);

  const suggestions = useMemo(() => {
    const pool = catalog.filter((entry) => !selectedSet.has(entry));
    const trimmed = draft.trim();
    if (!trimmed) return pool;
    const needle = trimmed.toUpperCase();
    return pool.filter(
      (entry) =>
        entry.includes(needle) ||
        formatTagLabel(entry).toUpperCase().includes(needle)
    );
  }, [catalog, draft, selectedSet]);

  /**
   * If the typed text would normalize to a value that's NOT already selected
   * and NOT in the catalog (so it isn't already offered as a suggestion),
   * surface a "Create '<text>'" row in the popover.
   */
  const createLabel = useMemo(() => {
    const trimmed = draft.trim();
    if (!trimmed) return null;
    const normalized = normalizeTagInput(trimmed);
    if (!normalized) return null;
    if (selectedSet.has(normalized)) return null;
    if (catalog.includes(normalized)) return null;
    return formatTagLabel(normalized);
  }, [catalog, draft, selectedSet]);

  const commit = useCallback(
    (text: string) => {
      const normalized = normalizeTagInput(text);
      if (normalized === null) return;
      if (selectedSet.has(normalized)) {
        setDraft('');
        return;
      }
      onChange([...value, normalized]);
      setDraft('');
    },
    [onChange, selectedSet, value]
  );

  const handleRemove = useCallback(
    (tag: string) => {
      onChange(value.filter((t) => t !== tag));
    },
    [onChange, value]
  );

  const handleRemoveLast = useCallback(() => {
    if (value.length === 0) return;
    onChange(value.slice(0, -1));
  }, [onChange, value]);

  const isEmpty = value.length === 0;

  return (
    <Panel title="Tags">
      <div className={styles.container}>
        {!isEmpty && (
          <TagChipRow>
            {value.map((tag) => (
              <TagChip
                key={tag}
                value={tag}
                label={formatTagLabel(tag)}
                removable
                disabled={disabled}
                onRemove={handleRemove}
              />
            ))}
          </TagChipRow>
        )}
        {isEmpty && !disabled && (
          <p className={styles.emptyHint}>
            No tags yet — type or pick from suggestions
          </p>
        )}
        <TagCombobox
          draft={draft}
          onDraftChange={setDraft}
          suggestions={suggestions}
          onCommit={commit}
          createLabel={createLabel}
          onRemoveLast={handleRemoveLast}
          onClearDraft={() => setDraft('')}
          disabled={disabled}
        />
      </div>
    </Panel>
  );
}

export default memo(TagsPanel);
